/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 * A-6 — LA ETIQUETA DE UN PROYECTO: "código — nombre", EN UN SOLO LUGAR.
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 *
 * En la práctica y en Sage los proyectos se identifican por su CÓDIGO (`119-PR01`, `123-AFD2`),
 * no por el nombre. El selector de proyecto al subir un recibo mostraba solo el nombre.
 *
 * 🔴 POR QUÉ UN HELPER Y NO DOS `<option>` MÁS. El formato estaba escrito **a mano en 10 sitios
 * de 7 archivos**, y agregar los dos del selector habría hecho 12. Existe el precedente al
 * lado: `formatGl.js` con `formatGlLabel`, creado en V37.1 para exactamente esto en el campo GL.
 *
 * ⚠⚠ Y AL MEDIR LAS COPIAS APARECIÓ QUE NO ERAN DOS VARIANTES, SINO CUATRO — con una que
 * difiere **en el caso normal**, no en un borde:
 *
 *   | # | forma                                        | sitios | code+name         |
 *   |---|----------------------------------------------|--------|-------------------|
 *   | A | `(code ? code+' — ' : '') + name`            | 5      | `119-PR01 — Villa`|
 *   | B | `code ? code+' — '+name : name`              | 1      | idéntica a A      |
 *   | C | A + `(name \|\| '—')`                          | 1      | idéntica a A      |
 *   | D | B + `(name \|\| '—')`                          | 1      | idéntica a A      |
 *   | E | `(code ? code+' ' : '') + name`  ← **espacio**| 4      | `119-PR01 Villa`  |
 *
 * 🔴 **La E no es un descuido: es una distinción de contexto.** Sus 4 sitios son ENCABEZADOS
 * (`h1`, `fontWeight` 700/600) y los de la A son `<option>` de dropdowns. Un título lee mejor
 * sin el guion. Unificar todo al em-dash habría **cambiado cuatro pantallas** que nadie pidió
 * tocar — por eso el separador es un parámetro y no una constante.
 *
 * ⚠ Sobre C y D (los `|| '—'`): solo difieren de A cuando el NOMBRE está vacío, y
 * **`projects.name` es `NOT NULL`** — medido: 0 de 16 proyectos sin nombre. O sea que el caso
 * donde divergen es inalcanzable para un proyecto. `sinNombre` existe igual porque las vistas
 * de Nómina reciben `project_name` de un `LEFT JOIN`, donde sí puede venir nulo.
 */

/**
 * @param {string|null|undefined} code   `projects.code` (nullable en el esquema)
 * @param {string|null|undefined} name   `projects.name`
 * @param {{sep?: string, sinNombre?: string}} [opciones]
 *   `sep`       — separador entre código y nombre. `' — '` (em-dash) para listas y dropdowns;
 *                 `' '` para encabezados. Ver la tabla de arriba: la diferencia es deliberada.
 *   `sinNombre` — qué mostrar si NO hay nombre. Por defecto `''`; las vistas que lo reciben de
 *                 un LEFT JOIN pasan `'—'`.
 */
export function etiquetaProyecto(code, name, { sep = ' — ', sinNombre = '' } = {}) {
  const c = (code ?? '').toString().trim();
  const n = (name ?? '').toString().trim();
  if (!c && !n) return sinNombre;
  if (!c) return n;
  if (!n) return sinNombre ? `${c}${sep}${sinNombre}` : c;
  return `${c}${sep}${n}`;
}

/** Atajo para encabezados: mismo helper, separador de espacio. Ver la variante E. */
export function etiquetaProyectoTitulo(code, name, opciones = {}) {
  return etiquetaProyecto(code, name, { sep: ' ', ...opciones });
}
